import { useCallback, useEffect, useRef, useState } from 'react';

// ────────────────────────────────────────────────────────────────────────────

export const useLineItemQuantityStepperState = (params: Params) => {
  const { quantity, onQuantityChangeStart, onQuantityChangeEnd, onQuantityChange } = params;

  // ─── Refs ─────────────────────────────────────────────────────────────────

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const previousQuantityRef = useRef(quantity);

  // ─── State ────────────────────────────────────────────────────────────────

  const [selectedQuantity, setSelectedQuantity] = useState(quantity);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const increaseQuantity = useCallback(() => {
    onQuantityChangeStart();
    setSelectedQuantity((currentQuantity) => currentQuantity + 1);
  }, [onQuantityChangeStart]);

  const decreaseQuantity = useCallback(() => {
    onQuantityChangeStart();
    setSelectedQuantity((currentQuantity) =>
      currentQuantity > 0 ? currentQuantity - 1 : currentQuantity,
    );
  }, [onQuantityChangeStart]);

  const resetQuantity = useCallback(() => {
    setSelectedQuantity(quantity);
  }, [quantity]);

  // ─── Effects ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const didQuantityChange = previousQuantityRef.current !== quantity;

    previousQuantityRef.current = quantity;

    if (didQuantityChange) {
      setSelectedQuantity(quantity);
    }
  }, [quantity]);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (selectedQuantity === quantity) {
      onQuantityChangeEnd();
      return;
    }

    timeoutRef.current = setTimeout(() => {
      onQuantityChange(selectedQuantity, resetQuantity);
      onQuantityChangeEnd();
    }, CHANGE_TIMEOUT_MS);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [quantity, resetQuantity, selectedQuantity, onQuantityChange, onQuantityChangeEnd]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    selectedQuantity,
    increaseQuantity,
    decreaseQuantity,
    resetQuantity,
  };
};

// ─── Constants ──────────────────────────────────────────────────────────────

const CHANGE_TIMEOUT_MS = 1000;

// ─── Types ──────────────────────────────────────────────────────────────────

type Params = {
  quantity: number;
  onQuantityChangeStart: () => void;
  onQuantityChangeEnd: () => void;
  onQuantityChange: (quantity: number, resetQuantity?: () => void) => void;
};
